import { useEffect, useRef, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box,
  IconButton,
  Typography,
  Paper,
  Tooltip,
  Menu,
  MenuItem,
  CircularProgress,
  Alert,
  Fade,
} from '@mui/material'
import {
  Keyboard as KeyboardIcon,
  ContentCopy as ClipboardIcon,
  Cloud as CloudIcon,
  Close as CloseIcon,
  MoreVert as MoreIcon,
  KeyboardCommandKey as WinOskIcon,
  Translate as LayoutIcon,
} from '@mui/icons-material'

// Guacamole types are global after the script loads
declare global {
  interface Window {
    Guacamole: any
  }
}

interface LaunchParams {
  server: string
  username: string
  password_encrypted: string
  initial_program?: string
  working_dir?: string
  width?: number
  height?: number
  dpi?: number
  app_name?: string
}

export default function Html5LaunchPage() {
  const { t } = useTranslation()
  const displayRef = useRef<HTMLDivElement>(null)
  const guacRef = useRef<any>(null)
  const tunnelRef = useRef<any>(null)
  const keyboardRef = useRef<any>(null)
  const touchKeyboardElRef = useRef<HTMLDivElement>(null)

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [connected, setConnected] = useState(false)
  const [keyboardVisible, setKeyboardVisible] = useState(false)
  const [layoutAnchor, setLayoutAnchor] = useState<null | HTMLElement>(null)
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null)
  const [statusText, setStatusText] = useState('')
  const [scriptLoaded, setScriptLoaded] = useState(false)

  // Read launch params from sessionStorage
  const params: LaunchParams | null = (() => {
    try {
      const raw = localStorage.getItem('html5_launch_params')
      return raw ? JSON.parse(raw) : null
    } catch {
      return null
    }
  })()

  // Load guacamole-common-js dynamically
  useEffect(() => {
    if (scriptLoaded || !params) return

    const existing = document.querySelector('script[data-guacamole="true"]')
    if (existing) {
      setScriptLoaded(true)
      return
    }

    const script = document.createElement('script')
    script.src = '/guacamole/all.min.js'
    script.async = true
    script.dataset.guacamole = 'true'
    script.onload = () => setScriptLoaded(true)
    script.onerror = () => setError(t('html5.error.scriptLoad'))
    document.head.appendChild(script)
  }, [params, scriptLoaded, t])

  // Initialize Guacamole client once script is loaded
  useEffect(() => {
    if (!scriptLoaded || !params || !displayRef.current || guacRef.current) return

    const Guacamole = window.Guacamole
    if (!Guacamole) {
      setError(t('html5.error.guacamoleNotLoaded'))
      setLoading(false)
      return
    }

    // Build WebSocket URL
    const isSecure = window.location.protocol === 'https:'
    const wsProtocol = isSecure ? 'wss:' : 'ws:'
    // Derive guacamole proxy subdomain from current host, e.g. myapps.flexilabs.net -> guacamole.myapps.flexilabs.net
    const hostname = window.location.hostname
    const guacamoleHost = hostname.includes('.')
      ? `guacamole.${hostname}`
      : hostname
    const wsBase = `${wsProtocol}//${guacamoleHost}`

    const query = new URLSearchParams()
    query.set('server', params.server)
    query.set('username', params.username)
    if (params.password_encrypted) query.set('password', params.password_encrypted)
    query.set('width', String(params.width || 1024))
    query.set('height', String(params.height || 768))
    query.set('dpi', String(params.dpi || 96))
    if (params.initial_program) query.set('initial_program', params.initial_program)
    if (params.working_dir) query.set('working_dir', params.working_dir)

    const wsUrl = `${wsBase}/GuacamoleTunnelServlet/WebSocket?${query.toString()}`

    try {
      const tunnel = new Guacamole.WebSocketTunnel(wsUrl)
      tunnelRef.current = tunnel

      const guac = new Guacamole.Client(tunnel)
      guacRef.current = guac

      // Add display to DOM
      const displayEl = guac.getDisplay().getElement()
      displayEl.style.width = '100%'
      displayEl.style.height = '100%'
      displayRef.current.appendChild(displayEl)

      // Connection state
      guac.onstatechange = (state: number) => {
        switch (state) {
          case 0: // idle
            setStatusText('')
            break
          case 1: // connecting
            setStatusText(t('html5.status.connecting'))
            break
          case 2: // waiting
            setStatusText(t('html5.status.waiting'))
            break
          case 3: // connected
            setStatusText('')
            setConnected(true)
            setLoading(false)
            break
          case 4: // disconnecting
            setStatusText(t('html5.status.disconnecting'))
            break
          case 5: // disconnected
            setStatusText(t('html5.status.disconnected'))
            setConnected(false)
            break
        }
      }

      // Error handler with auto-reconnect
      let reconnectAttempts = 0
      const maxReconnect = 3
      guac.onerror = (err: any) => {
        console.error('[Guacamole] error:', err)
        if (err.code === 521) {
          // Disconnected by other session — do not reconnect
          setError(err.message || t('html5.error.sessionTaken'))
          setLoading(false)
        } else if (reconnectAttempts < maxReconnect) {
          reconnectAttempts++
          setError(t('html5.error.reconnecting', { count: reconnectAttempts }))
          setTimeout(() => {
            if (guacRef.current) {
              guacRef.current.connect()
            }
          }, 2000)
        } else {
          setError(t('html5.error.connectionFailed'))
          setLoading(false)
        }
      }

      // Audio
      const AUDIO_MIMETYPE = 'audio/L16;rate=44100,channels=2'
      const requestAudioStream = () => {
        if (!guacRef.current) return
        const stream = guacRef.current.createAudioStream(AUDIO_MIMETYPE)
        const recorder = Guacamole.AudioRecorder.getInstance(stream, AUDIO_MIMETYPE)
        if (!recorder) {
          stream.sendEnd()
        } else {
          recorder.onclose = requestAudioStream
        }
      }
      guac.onstatechange = (state: number) => {
        if (state === 3) requestAudioStream()
      }

      // Mouse
      const mouse = new Guacamole.Mouse(guac.getDisplay().getElement())
      mouse.onmousedown = mouse.onmouseup = mouse.onmousemove = (ms: any) => {
        guac.sendMouseState(ms)
      }

      // Touchscreen
      const touch = new Guacamole.Mouse.Touchscreen(guac.getDisplay().getElement())
      touch.onmousedown = touch.onmouseup = touch.onmousemove = (ms: any) => {
        guac.sendMouseState(ms)
      }

      // Keyboard
      const keyboard = new Guacamole.Keyboard(document)
      keyboard.onkeydown = (keysym: number) => guac.sendKeyEvent(1, keysym)
      keyboard.onkeyup = (keysym: number) => guac.sendKeyEvent(0, keysym)
      keyboardRef.current = keyboard

      // Clipboard receive
      guac.onclipboard = (stream: any, mimetype: string) => {
        if (!/^text\//.exec(mimetype)) return
        const reader = new Guacamole.StringReader(stream)
        let text = ''
        reader.ontext = (chunk: string) => {
          text += chunk
        }
        reader.onend = () => {
          navigator.clipboard.writeText(text).catch(() => {})
        }
      }

      // Connect
      guac.connect()
      setStatusText(t('html5.status.connecting'))
    } catch (exc: any) {
      console.error('[Html5Launch] init error:', exc)
      setError(t('html5.error.init') + ': ' + (exc.message || String(exc)))
      setLoading(false)
    }

    // Resize handler
    const handleResize = () => {
      if (!guacRef.current || !displayRef.current) return
      const w = Math.floor(window.innerWidth)
      const h = Math.floor(window.innerHeight - 48) // subtract menu bar height
      guacRef.current.sendSize(w, h)
    }
    window.addEventListener('resize', handleResize)
    handleResize()

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize)
      if (guacRef.current) {
        try {
          guacRef.current.disconnect()
        } catch {
          // ignore
        }
        guacRef.current = null
      }
      if (tunnelRef.current) {
        tunnelRef.current = null
      }
      if (displayRef.current) {
        displayRef.current.innerHTML = ''
      }
    }
  }, [scriptLoaded, params, t])

  const LAYOUTS = [
    { key: 'en-us', label: 'English (US)', file: '/guacamole/layouts/en-us-qwerty.json' },
    { key: 'es-es', label: 'Español', file: '/guacamole/layouts/es-es-qwerty.json' },
    { key: 'de-de', label: 'Deutsch', file: '/guacamole/layouts/de-de-qwertz.json' },
    { key: 'fr-fr', label: 'Français', file: '/guacamole/layouts/fr-fr-azerty.json' },
    { key: 'it-it', label: 'Italiano', file: '/guacamole/layouts/it-it-qwerty.json' },
    { key: 'nl-nl', label: 'Nederlands', file: '/guacamole/layouts/nl-nl-qwerty.json' },
    { key: 'ru-ru', label: 'Русский', file: '/guacamole/layouts/ru-ru-qwerty.json' },
  ]

  const detectLayout = (): string => {
    const lang = navigator.language.toLowerCase()
    if (lang.startsWith('es')) return 'es-es'
    if (lang.startsWith('de')) return 'de-de'
    if (lang.startsWith('fr')) return 'fr-fr'
    if (lang.startsWith('it')) return 'it-it'
    if (lang.startsWith('nl')) return 'nl-nl'
    if (lang.startsWith('ru')) return 'ru-ru'
    return 'en-us'
  }

  // Action handlers
  const sendCtrlAltDel = useCallback(() => {
    if (!guacRef.current) return
    const g = guacRef.current
    g.sendKeyEvent(1, 0xffe3) // Ctrl
    g.sendKeyEvent(1, 0xffe9) // Alt
    g.sendKeyEvent(1, 0xffff) // Delete
    g.sendKeyEvent(0, 0xffff)
    g.sendKeyEvent(0, 0xffe9)
    g.sendKeyEvent(0, 0xffe3)
  }, [])

  const sendAltTab = useCallback(() => {
    if (!guacRef.current) return
    const g = guacRef.current
    g.sendKeyEvent(1, 0xffe9) // Alt
    g.sendKeyEvent(1, 0xff09) // Tab
    g.sendKeyEvent(0, 0xff09)
    g.sendKeyEvent(0, 0xffe9)
  }, [])

  const sendWinOsk = useCallback(() => {
    if (!guacRef.current) return
    const g = guacRef.current
    const chars = 'osk.exe'
    // Win+R
    g.sendKeyEvent(1, 0xffeb) // Win down
    g.sendKeyEvent(1, 0x0072) // R down
    g.sendKeyEvent(0, 0x0072) // R up
    g.sendKeyEvent(0, 0xffeb) // Win up
    // Wait for Run dialog, then type osk.exe
    setTimeout(() => {
      for (let i = 0; i < chars.length; i++) {
        const code = chars.charCodeAt(i)
        const keysym = code >= 32 && code <= 126 ? code : 0
        if (keysym) {
          g.sendKeyEvent(1, keysym)
          g.sendKeyEvent(0, keysym)
        }
      }
      // Enter
      g.sendKeyEvent(1, 0xff0d)
      g.sendKeyEvent(0, 0xff0d)
    }, 500)
  }, [])

  const showKeyboard = useCallback((layoutKey?: string) => {
    if (!window.Guacamole) return
    const Guacamole = window.Guacamole
    const chosen = layoutKey || detectLayout()
    const layoutDef = LAYOUTS.find((l) => l.key === chosen) || LAYOUTS[0]

    fetch(layoutDef.file)
      .then((r) => r.json())
      .then((layout) => {
        const osk = new Guacamole.OnScreenKeyboard(layout)
        const el = osk.getElement()
        el.style.position = 'fixed'
        el.style.bottom = '0'
        el.style.left = '0'
        el.style.right = '0'
        el.style.width = '100%'
        el.style.maxWidth = '100vw'
        el.style.zIndex = '1000'
        el.style.background = '#222'
        el.style.overflow = 'hidden'
        if (touchKeyboardElRef.current) {
          touchKeyboardElRef.current.appendChild(el)
        } else {
          document.body.appendChild(el)
        }
        osk.onkeydown = (keysym: number) => {
          if (guacRef.current) guacRef.current.sendKeyEvent(1, keysym)
        }
        osk.onkeyup = (keysym: number) => {
          if (guacRef.current) guacRef.current.sendKeyEvent(0, keysym)
        }
        // Resize display to make room
        if (guacRef.current) {
          const w = Math.floor(window.innerWidth)
          const h = Math.floor(window.innerHeight - 48 - el.offsetHeight)
          guacRef.current.sendSize(w, h)
        }
      })
      .catch(() => setError(t('html5.error.keyboardLayout')))
  }, [t])

  const hideKeyboard = useCallback(() => {
    if (touchKeyboardElRef.current) {
      touchKeyboardElRef.current.innerHTML = ''
    }
    // Reset any stuck modifiers
    if (keyboardRef.current) {
      keyboardRef.current.reset()
    }
    const w = Math.floor(window.innerWidth)
    const h = Math.floor(window.innerHeight - 48)
    if (guacRef.current) guacRef.current.sendSize(w, h)
  }, [])

  const toggleKeyboard = useCallback(() => {
    setKeyboardVisible((prev) => {
      if (prev) {
        hideKeyboard()
      } else {
        showKeyboard()
      }
      return !prev
    })
  }, [hideKeyboard, showKeyboard])

  const changeLayout = useCallback((layoutKey: string) => {
    if (!keyboardVisible) return
    hideKeyboard()
    showKeyboard(layoutKey)
    setLayoutAnchor(null)
  }, [keyboardVisible, hideKeyboard, showKeyboard])

  const sendClipboard = useCallback(() => {
    if (!guacRef.current || !window.Guacamole) return
    navigator.clipboard
      .readText()
      .then((text) => {
        if (!text) return
        const stream = guacRef.current.createClipboardStream('text/plain')
        const writer = new window.Guacamole.StringWriter(stream)
        for (let i = 0; i < text.length; i += 4096) {
          writer.sendText(text.substring(i, i + 4096))
        }
        writer.sendEnd()
        guacRef.current.setClipboard(text)
      })
      .catch(() => {
        // ignore clipboard permission errors
      })
  }, [])

  const closeSession = useCallback(() => {
    if (guacRef.current) {
      try {
        guacRef.current.disconnect()
      } catch {
        // ignore
      }
    }
    window.close()
  }, [])

  if (!params) {
    return (
      <Box
        sx={{
          width: '100vw',
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: '#000',
        }}
      >
        <Alert severity="error">{t('html5.error.noParams')}</Alert>
      </Box>
    )
  }

  return (
    <Box
      sx={{
        width: '100vw',
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#000',
        overflow: 'hidden',
      }}
    >
      {/* Top menu bar */}
      <Paper
        elevation={2}
        sx={{
          height: 48,
          display: 'flex',
          alignItems: 'center',
          px: 1,
          gap: 0.5,
          borderRadius: 0,
          bgcolor: '#f5f0e6',
          color: '#4a3b2a',
          zIndex: 50,
          flexShrink: 0,
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 700,
            mr: 1,
            flexShrink: 0,
            color: '#d35400',
            letterSpacing: 0.5,
          }}
        >
          {params.app_name || t('html5.title')}
        </Typography>

        <Box sx={{ flexGrow: 1 }} />

        {/* Desktop controls */}
        <Box sx={{ display: { xs: 'none', sm: 'flex' }, gap: 0.5, alignItems: 'center' }}>
          <Tooltip title="Ctrl+Alt+Supr">
            <IconButton
              size="small"
              onClick={sendCtrlAltDel}
              sx={{ color: '#4a3b2a', '&:hover': { bgcolor: 'rgba(211,84,0,0.12)' } }}
            >
              <Typography variant="caption" sx={{ fontWeight: 700, fontSize: 11 }}>CAD</Typography>
            </IconButton>
          </Tooltip>

          <Tooltip title="Alt+Tab">
            <IconButton
              size="small"
              onClick={sendAltTab}
              sx={{ color: '#4a3b2a', '&:hover': { bgcolor: 'rgba(211,84,0,0.12)' } }}
            >
              <Typography variant="caption" sx={{ fontWeight: 700, fontSize: 11 }}>Alt+Tab</Typography>
            </IconButton>
          </Tooltip>

          <Tooltip title={t('html5.menu.keyboard')}>
            <IconButton
              size="small"
              onClick={toggleKeyboard}
              sx={{
                color: keyboardVisible ? '#d35400' : '#4a3b2a',
                '&:hover': { bgcolor: 'rgba(211,84,0,0.12)' },
              }}
            >
              <KeyboardIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          {keyboardVisible && (
            <Tooltip title={t('html5.menu.changeLayout')}>
              <IconButton
                size="small"
                onClick={(e) => setLayoutAnchor(e.currentTarget)}
                sx={{ color: '#4a3b2a', '&:hover': { bgcolor: 'rgba(211,84,0,0.12)' } }}
              >
                <LayoutIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}

          <Tooltip title={t('html5.menu.winOsk')}>
            <IconButton
              size="small"
              onClick={sendWinOsk}
              sx={{ color: '#4a3b2a', '&:hover': { bgcolor: 'rgba(211,84,0,0.12)' } }}
            >
              <WinOskIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title={t('html5.menu.clipboard')}>
            <IconButton
              size="small"
              onClick={sendClipboard}
              sx={{ color: '#4a3b2a', '&:hover': { bgcolor: 'rgba(211,84,0,0.12)' } }}
            >
              <ClipboardIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title={t('html5.menu.myCloudFiles')}>
            <span>
              <IconButton
                size="small"
                disabled
                sx={{ color: '#aaa', '&:hover': { bgcolor: 'transparent' } }}
              >
                <CloudIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip title={t('html5.menu.close')}>
            <IconButton
              size="small"
              onClick={closeSession}
              sx={{ color: '#c0392b', '&:hover': { bgcolor: 'rgba(192,57,43,0.12)' } }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Layout selector dropdown */}
        <Menu
          anchorEl={layoutAnchor}
          open={Boolean(layoutAnchor)}
          onClose={() => setLayoutAnchor(null)}
          TransitionComponent={Fade}
        >
          {LAYOUTS.map((l) => (
            <MenuItem key={l.key} onClick={() => changeLayout(l.key)}>
              {l.label}
            </MenuItem>
          ))}
        </Menu>

        {/* Mobile hamburger */}
        <Box sx={{ display: { xs: 'flex', sm: 'none' } }}>
          <IconButton size="small" onClick={(e) => setMenuAnchor(e.currentTarget)}>
            <MoreIcon fontSize="small" sx={{ color: '#4a3b2a' }} />
          </IconButton>
          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={() => setMenuAnchor(null)}
            TransitionComponent={Fade}
          >
            <MenuItem onClick={() => { sendCtrlAltDel(); setMenuAnchor(null) }}>Ctrl+Alt+Supr</MenuItem>
            <MenuItem onClick={() => { sendAltTab(); setMenuAnchor(null) }}>Alt+Tab</MenuItem>
            <MenuItem onClick={() => { toggleKeyboard(); setMenuAnchor(null) }}>{t('html5.menu.keyboard')}</MenuItem>
            <MenuItem onClick={() => { sendWinOsk(); setMenuAnchor(null) }}>{t('html5.menu.winOsk')}</MenuItem>
            <MenuItem onClick={() => { sendClipboard(); setMenuAnchor(null) }}>{t('html5.menu.clipboard')}</MenuItem>
            <MenuItem disabled>{t('html5.menu.myCloudFiles')}</MenuItem>
            <MenuItem onClick={() => { closeSession(); setMenuAnchor(null) }}>{t('html5.menu.close')}</MenuItem>
          </Menu>
        </Box>

        {/* Status text */}
        {statusText && (
          <Typography
            variant="caption"
            sx={{
              ml: 1,
              color: connected ? '#27ae60' : '#e67e22',
              fontWeight: 600,
              fontSize: 11,
            }}
          >
            {statusText}
          </Typography>
        )}
      </Paper>

      {/* Guacamole display */}
      <Box
        ref={displayRef}
        sx={{
          flexGrow: 1,
          position: 'relative',
          bgcolor: '#000',
          overflow: 'hidden',
        }}
      >
        {loading && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10,
            }}
          >
            <CircularProgress sx={{ color: '#d35400' }} />
          </Box>
        )}

        {error && (
          <Box
            sx={{
              position: 'absolute',
              top: 16,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 20,
              maxWidth: 480,
              width: '90%',
            }}
          >
            <Alert
              severity="error"
              onClose={() => setError('')}
              sx={{ bgcolor: 'rgba(44,62,80,0.95)', color: '#fff' }}
            >
              {error}
            </Alert>
          </Box>
        )}
      </Box>

      {/* On-screen keyboard container */}
      <div ref={touchKeyboardElRef} />
    </Box>
  )
}
